import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useBackend } from '@/composables'

const STORE_NAME = 'positionConfig'

/**
 * Store for caching position metric configurations.
 * Loads all positions with their metric requirements once,
 * then provides O(1) lookup by positionId.
 */
export const usePositionConfigStore = defineStore(STORE_NAME, () => {
    // Map of positionId -> { positionName, metrics: [...] }
    const configs = ref({})
    const loading = ref(false)
    const lastLoadedAt = ref(null)
    const lastError = ref(null)

    const { items, getItems, error: backendError } = useBackend('payroll/positions/metrics-config')

    /**
     * Load all position metric configurations from the server.
     * @param {boolean} force - Force reload even if already loaded
     */
    const loadAll = async (force = false) => {
        // Skip if already loaded and not forcing
        if (!force && Object.keys(configs.value).length > 0) {
            return
        }

        // Skip if already loading
        if (loading.value) {
            return
        }

        loading.value = true
        lastError.value = null

        await getItems()

        if (backendError.value) {
            lastError.value = backendError.value
            console.error('Failed to load position metric configs:', backendError.value)
        } else if (items.value) {
            // Convert array to map by positionId for O(1) lookup
            configs.value = items.value.reduce((acc, pos) => {
                acc[pos.positionId] = {
                    positionName: pos.positionName,
                    metrics: pos.metrics || []
                }
                return acc
            }, {})
            lastLoadedAt.value = new Date().toISOString()
        }

        loading.value = false
    }

    /**
     * Ensure configs are loaded. Safe to call multiple times.
     */
    const ensureLoaded = async () => {
        if (Object.keys(configs.value).length === 0 && !loading.value) {
            await loadAll()
        }
    }

    /**
     * Get metrics configuration for a specific position.
     * @param {number} positionId - The position ID to look up
     * @returns {Array} Array of metric configurations, or empty array if not found
     */
    const getMetricsForPosition = (positionId) => {
        if (!positionId) return []
        return configs.value[positionId]?.metrics || []
    }

    /**
     * Get position name by ID.
     * @param {number} positionId - The position ID to look up
     * @returns {string|null} Position name or null if not found
     */
    const getPositionName = (positionId) => {
        if (!positionId) return null
        return configs.value[positionId]?.positionName || null
    }

    /**
     * Reset the store (e.g., on logout or tenant switch).
     */
    const reset = () => {
        configs.value = {}
        lastLoadedAt.value = null
        lastError.value = null
    }

    return {
        configs,
        loading,
        lastLoadedAt,
        lastError,
        loadAll,
        ensureLoaded,
        getMetricsForPosition,
        getPositionName,
        reset
    }
})
// Note: No persist option - reloads each session
